import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Cancel component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconCancel extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M5.90834 4.84768C7.54864 3.44917 9.67597 2.60498 12.0005 2.60498C17.1893 2.60498 21.3955 6.81126 21.3955 12C21.3955 14.3245 20.5513 16.4519 19.1528 18.0921L5.90834 4.84768ZM4.37842 4.21503C4.34767 4.23754 4.31825 4.26269 4.29047 4.29047C4.26268 4.31827 4.23752 4.3477 4.215 4.37846C2.29136 6.34322 1.10547 9.03311 1.10547 12C1.10547 18.0172 5.98333 22.895 12.0005 22.895C14.9667 22.895 17.6561 21.7096 19.6207 19.7868C19.652 19.764 19.6819 19.7385 19.7102 19.7102C19.7384 19.682 19.7639 19.652 19.7867 19.6207C21.7099 17.6561 22.8955 14.9665 22.8955 12C22.8955 5.98284 18.0177 1.10498 12.0005 1.10498C9.03333 1.10498 6.34322 2.29109 4.37842 4.21503ZM18.0921 19.1528C16.4519 20.551 14.3247 21.395 12.0005 21.395C6.81175 21.395 2.60547 17.1888 2.60547 12C2.60547 9.67573 3.44947 7.54861 4.84772 5.90838L18.0921 19.1528Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-cancel') === undefined) {
  customElements.define('cat-icon-cancel', BlocksIconCancel);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-cancel': BlocksIconCancel;
  }
}
